import { Box, Typography } from "@mui/material";
import { formatDate } from "../../../../../../utils/helpers";
import { TemplateRevision } from "../types";
import { tokens } from "../tokens";
import UserAvatar from "./UserAvatar";

interface Props {
  revision: TemplateRevision;
  /** Hides the connecting rail under the last row of the list. */
  isLast: boolean;
  onView: (revision: TemplateRevision) => void;
}

const currentTagSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.03em",
  textTransform: "uppercase" as const,
  borderRadius: "4px",
  padding: "2px 6px",
  lineHeight: 1.6,
  backgroundColor: tokens.successSoft,
  color: tokens.success,
};

const RevisionItem = ({ revision, isLast, onView }: Props) => (
  <Box
    role="button"
    tabIndex={0}
    aria-label={`View revision from ${formatDate(revision.modifiedAt)}`}
    onClick={() => onView(revision)}
    data-tracker="1085"
    onKeyDown={(event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onView(revision);
      }
    }}
    sx={{
      display: "flex",
      gap: 1.5,
      padding: "13px 10px",
      borderRadius: "8px",
      border: "1px solid transparent",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: tokens.bg,
        borderColor: tokens.border,
      },
      "&:focus-visible": {
        outline: `2px solid ${tokens.accent}`,
        outlineOffset: "2px",
      },
    }}
  >
    {/* Timeline rail */}
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: "3px",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          border: `2px solid ${tokens.surface}`,
          boxShadow: `0 0 0 1px ${tokens.borderStrong}`,
          backgroundColor:
            revision.action === "CREATE" ? tokens.success : tokens.info,
        }}
      />
      {!isLast && (
        <Box
          sx={{
            width: "1.5px",
            flex: 1,
            backgroundColor: tokens.border,
            mt: "4px",
          }}
        />
      )}
    </Box>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          mb: 0.6,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.text }}>
          {revision.name}
        </Typography>
        {revision.isCurrent && (
          <Box component="span" sx={currentTagSx}>
            Latest Revision
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: 12,
          color: tokens.textMuted,
          mt: "3px",
        }}
      >
        <UserAvatar email={revision.modifiedBy} />
        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
          Modified by{" "}
          <Box component="strong" sx={{ color: tokens.text }}>
            {revision.modifiedBy}
          </Box>{" "}
          · {formatDate(revision.modifiedAt)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: "3px",
        }}
      >
        <UserAvatar email={revision.createdBy} />
        <Typography sx={{ fontSize: 12, color: tokens.textFaint }}>
          Created by {revision.createdBy} · {formatDate(revision.createdAt)}
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default RevisionItem;
